import { isFuture } from "date-fns";

import { AssessmentStatus } from "api/api-data-types";
import {
  ALL_ASSESSMENT_REPORT_FULL_HEADER_MAP,
  ALL_ASSESSMENT_REPORT_REDUCED_HEADER_MAP,
  AllAssessmentReportHeader,
  AllAssessmentTableRow,
  AssessmentPageType,
  SECTION_SCORE_REPORT_FULL_HEADER_MAP,
  SECTION_SCORE_REPORT_REDUCED_HEADER_MAP,
  SectionScoreReportHeader,
} from "api/api-types-assessment";
import type { PageGroupUserType, PageOutcomeType, SubmissionType } from "components/server-types";
import { relativeDayAndTimeFormat } from "i18n/helpers";
import { t } from "i18n/i18n";
import {
  AssessmentPageTypeKey,
  AssessmentPublishStatusKey,
  AssessmentStatusKey,
} from "i18n/i18next";
import { isIndividualSubmission } from "store/selectors/type-helpers";
import type { NestedAssessmentType } from "store/selectors/types";
import { CSVData } from "utils/file";
import { averageAndRound } from "utils/math";
import { CSVHeaderMap, translateCSVHeaders } from "utils/reporting";

export interface PageAssessmentData {
  pointList: (number | undefined | null)[];
  submissions: SubmissionType[];
  latestSubmissionDate: string | false;
  pageSpecificOutcomes: PageOutcomeType[];
}

export interface StudentAssessmentFilter {
  section_user_id: string;
  page_group_id?: string;
}

export const getPageSpecificAssessmentData = (
  page: { assessments: NestedAssessmentType[]; id: string },
  pageOutcomes: PageOutcomeType[],
  tableDateFormat: (date: string) => string,
): PageAssessmentData => {
  const pointList = page.assessments.map((a) => a.points).filter((points) => points != null);
  const submissions = page.assessments.map((a) => a.submission);
  const submissionsByDate = submissions
    .slice(0)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const latestSubmissionDate =
    submissionsByDate.length > 0 && tableDateFormat(submissionsByDate[0].created_at);
  const pageSpecificOutcomes = pageOutcomes.filter((po) => po.page_id === page.id);

  return {
    pointList,
    submissions,
    latestSubmissionDate,
    pageSpecificOutcomes,
  };
};

export const filterAssessmentsForStudent = (
  pageAssessments: NestedAssessmentType[],
  student: StudentAssessmentFilter,
  pageGroupUsers: PageGroupUserType[],
): NestedAssessmentType[] => {
  return pageAssessments.filter((a) => {
    const submission = a.submission;
    return isIndividualSubmission(submission)
      ? submission.section_user_id === student.section_user_id
      : pageGroupUsers.find(
          (pgu) =>
            pgu.page_group_id === submission.page_group_id &&
            pgu.section_user_id === student.section_user_id,
        );
  });
};

export const calculateStudentOutcomeScore = (
  userAssessments: NestedAssessmentType[],
  sectionOutcomeId: string,
  pageOutcomes: PageOutcomeType[],
): number | null => {
  const userOutcomeAssessmentsForThisOutcome = userAssessments
    .map((a) => a.outcome_assessments || [])
    .flat()
    .filter(
      (oa) =>
        pageOutcomes.find((po) => po.id === oa.page_outcome_id)?.section_outcome_id ===
          sectionOutcomeId && oa.score != null,
    );

  return userOutcomeAssessmentsForThisOutcome.length
    ? averageAndRound(userOutcomeAssessmentsForThisOutcome.map((oa) => oa.score))
    : null;
};

/**
 * Returns the localized assessment status label based on completion state.
 * Used across assessment views to display consistent status text.
 *
 * Note: Callers should check for NOT_ASSESSABLE status before calling this function
 * if needed. HomeAssessmentList doesn't need this check (server pre-filters),
 * while SectionScoreDashboard and hooks.ts do.
 */
export const getAssessmentStatusLabel = (
  isMarkedComplete: boolean,
  assessedSubmissionsCount: number,
  isAssessmentComplete: boolean,
): string => {
  if (isMarkedComplete) {
    return assessedSubmissionsCount === 0 ? t("common.completed") : t("assessment_status.assessed");
  }

  return isAssessmentComplete ? t("assessment_status.assessed") : t("assessment_status.assessable");
};

export const formatAssessmentPageType = (pageType: AssessmentPageType): string =>
  t(`page_types.${pageType as AssessmentPageTypeKey}`);

const translateAllAssessmentReportStatusValue = (value: string): string => {
  if (value === "-") {
    return value;
  }
  if (value === "completed") {
    return t("common.completed");
  }
  return t(`assessment_status.${value as AssessmentStatusKey}`);
};

const translateAllAssessmentReportPageTypeValue = (value: AssessmentPageTypeKey): string =>
  t(`page_types.${value}`);

const translateAllAssessmentReportPublishingValue = (value: AssessmentPublishStatusKey): string =>
  t(`assessment_dashboard.publish_status.${value}`);

const translateAllAssessmentReportValues = (csvData: CSVData): CSVData => {
  const statusIdx = csvData.headers.indexOf(AllAssessmentReportHeader.status);
  const pageTypeIdx = csvData.headers.indexOf(AllAssessmentReportHeader.page_type);
  const publishingIdx = csvData.headers.indexOf(AllAssessmentReportHeader.publishing);

  if (statusIdx === -1 && pageTypeIdx === -1 && publishingIdx === -1) {
    return csvData;
  }

  return {
    headers: csvData.headers,
    rows: csvData.rows.map((row) => {
      const translatedRow = [...row];

      if (statusIdx !== -1) {
        translatedRow[statusIdx] = translateAllAssessmentReportStatusValue(
          String(translatedRow[statusIdx]),
        );
      }

      if (pageTypeIdx !== -1) {
        translatedRow[pageTypeIdx] = translateAllAssessmentReportPageTypeValue(
          translatedRow[pageTypeIdx] as AssessmentPageTypeKey,
        );
      }

      if (publishingIdx !== -1) {
        translatedRow[publishingIdx] = translateAllAssessmentReportPublishingValue(
          translatedRow[publishingIdx] as AssessmentPublishStatusKey,
        );
      }

      return translatedRow;
    }),
  };
};

export const translateSectionScoreReport = (csvData: CSVData, sectionScope: boolean): CSVData => {
  const headerMap = (
    sectionScope ? SECTION_SCORE_REPORT_REDUCED_HEADER_MAP : SECTION_SCORE_REPORT_FULL_HEADER_MAP
  ) as CSVHeaderMap<SectionScoreReportHeader>;

  return translateCSVHeaders(csvData as CSVData<SectionScoreReportHeader>, headerMap);
};

export const translateAllAssessmentReport = (csvData: CSVData, sectionScope: boolean): CSVData => {
  const headerMap = (
    sectionScope ? ALL_ASSESSMENT_REPORT_REDUCED_HEADER_MAP : ALL_ASSESSMENT_REPORT_FULL_HEADER_MAP
  ) as CSVHeaderMap<AllAssessmentReportHeader>;
  const withTranslatedValues = translateAllAssessmentReportValues(csvData);

  return translateCSVHeaders(withTranslatedValues as CSVData<AllAssessmentReportHeader>, headerMap);
};

export const mapAllAssessmentTableRow = (row: AllAssessmentTableRow) => ({
  id: row.page_id,
  hidden: row.hidden,
  title: row.title,
  topicTitle: row.topic_title ?? null,
  dueAt: row.due_at,
  submissionsLastCollectedAt: row.submissions_last_collected_at,
  status: row.status as AssessmentStatus,
  unassessedLateCount: row.unassessed_late_count,
  assessedSubmissionsCount: row.assessed_submissions_count,
  totalSubmissionsCount: row.total_submissions_count,
  isMarkedComplete: row.is_marked_complete,
  isAssessmentComplete: row.is_assessment_complete,
  publishStatus: (["published", "draft"].includes(row.publishing)
    ? row.publishing
    : "na") as AssessmentPublishStatusKey,
  assessedCount: row.assessed_count,
  pageType: formatAssessmentPageType(row.page_type),
  avgPoints: row.avg_points,
  outcomesCount: row.outcomes_count,
  outcomesTooltip: row.outcomes_tooltip,
  lastSubmittedDate: row.last_submitted_date || "-",
});

export type MappedAllAssessmentTableRow = ReturnType<typeof mapAllAssessmentTableRow>;

export const isOpenForSubmissionRow = (
  row: Pick<
    MappedAllAssessmentTableRow,
    "totalSubmissionsCount" | "dueAt" | "submissionsLastCollectedAt"
  >,
) =>
  row.totalSubmissionsCount === 0 &&
  Boolean(row.dueAt && isFuture(row.dueAt)) &&
  !row.submissionsLastCollectedAt;

export const partitionAllAssessmentDashboardRows = (
  allAssessmentsTableRows: MappedAllAssessmentTableRow[],
) => {
  const openForSubmissionsRows: MappedAllAssessmentTableRow[] = [];
  const assessableRows: MappedAllAssessmentTableRow[] = [];
  const assessedRows: MappedAllAssessmentTableRow[] = [];

  for (const row of allAssessmentsTableRows) {
    if (isOpenForSubmissionRow(row)) {
      openForSubmissionsRows.push({
        ...row,
        publishStatus: "na",
        avgPoints: "-",
        lastSubmittedDate: "-",
      });
    } else if (row.totalSubmissionsCount === 0) {
      // Do nothing
    } else if (
      row.status === AssessmentStatus.ASSESSABLE &&
      !row.isAssessmentComplete &&
      !row.isMarkedComplete
    ) {
      assessableRows.push(row);
    } else {
      assessedRows.push(row);
    }
  }

  return { openForSubmissionsRows, assessableRows, assessedRows };
};

export const buildOpenForSubmissionCSVData = (rows: MappedAllAssessmentTableRow[]) => {
  const headers = [
    t("assessment_dashboard.all.page_topic_header_title"),
    t("assessment_dashboard.all.page_header_title"),
    t("assessment_dashboard.all.status_header_title"),
    t("assessment_dashboard.all.publishing_header_title"),
    t("assessment_dashboard.all.assessed_header_title"),
    t("assessment_dashboard.all.page_type_header_title"),
    t("assessment_dashboard.all.avg_points_header_title"),
    t("assessment_dashboard.all.outcomes_header_title"),
    t("assessment_dashboard.all.last_submitted_header_title"),
  ];

  const csvRows = rows.map((row) => [
    row.topicTitle || "-",
    row.title,
    row.dueAt ? relativeDayAndTimeFormat(new Date(row.dueAt)) : "-",
    "-",
    "-",
    row.pageType,
    "-",
    row.outcomesCount,
    "-",
  ]);

  return { headers, rows: csvRows };
};
